import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Container,
  Heading,
  SimpleGrid,
  Box,
  Text,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useColorModeValue,
  useToast,
  Code,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { useWallet } from '../context/WalletContext';
import BlindboxCard from '../components/BlindboxCard';
import StatsCard from '../components/StatsCard';
import TransactionList from '../components/TransactionList';
import BlindboxRatesStats from '../components/BlindboxRatesStats';
import blindboxService, { BlindboxStats, BlindboxItem } from '../services/blindboxService';
import { FaCubes, FaPercentage, FaSyncAlt } from 'react-icons/fa';

const BlindboxPage: React.FC = () => {
  const { walletAddress } = useWallet();
  const toast = useToast();
  
  // Local state management instead of useApiQuery
  const [stats, setStats] = useState<BlindboxStats | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Use refs to track mounted state and prevent race conditions
  const isMounted = useRef(true);
  const isRequestInProgress = useRef(false);
  const renderCount = useRef(0);
  
  // Log component lifecycle for debugging
  useEffect(() => {
    renderCount.current += 1;
    console.log(`BlindboxPage render #${renderCount.current}`);
    
    return () => {
      console.log(`BlindboxPage unmounting after render #${renderCount.current}`);
      isMounted.current = false;
    };
  }, []);
  
  // API health check
  useEffect(() => {
    console.log("BlindboxPage mounted, checking API connection...");
    
    // Only run health check once
    const healthCheck = async () => {
      try {
        const response = await fetch('/api/health');
        console.log("API health check response:", response.status);
        
        if (!response.ok) {
          throw new Error(`Health check failed with status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log("API health check successful:", data);
      } catch (error) {
        console.error("API health check error:", error);
        if (isMounted.current) {
          toast({
            title: "API Connection Issue",
            description: `Could not connect to backend: ${error instanceof Error ? error.message : 'Unknown error'}`,
            status: "error",
            duration: 7000,
            isClosable: true,
          });
        }
      }
    };
    
    healthCheck();
  }, [toast]);
  
  // Function to fetch stats data
  const fetchStats = useCallback(async () => {
    // Prevent concurrent requests
    if (isRequestInProgress.current) {
      console.log("BlindboxPage: Request already in progress, skipping");
      return;
    }
    
    console.log("BlindboxPage: Fetching blindbox stats...");
    isRequestInProgress.current = true;
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("Making API call to blindboxService.getBlindboxStats()...");
      const result = await blindboxService.getBlindboxStats();
      console.log("Stats fetched successfully:", result);
      
      if (isMounted.current) {
        setStats(result);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
      
      if (isMounted.current) {
        setError(error instanceof Error ? error : new Error('Unknown error occurred'));
        setIsLoading(false);
        
        toast({
          title: "Failed to load stats",
          description: error instanceof Error ? error.message : "Unknown error",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      isRequestInProgress.current = false;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Initial data fetch
  useEffect(() => {
    // Set up cleanup for component unmount
    isMounted.current = true;
    
    fetchStats();
    
    return () => {
      isMounted.current = false;
    };
  }, [fetchStats]);
  
  const boxBg = useColorModeValue('white', 'gray.700');

  // Format number with 2 decimal places
  const formatNumber = (num: number): string => {
    return num.toFixed(2);
  };
  
  // Purchase complete handler
  const handlePurchaseComplete = useCallback(() => {
    console.log("Purchase complete, refreshing stats...");
    // Use a slight delay before fetching to ensure UI rendering is completed first
    setTimeout(() => {
      if (isMounted.current) {
        console.log("Delayed stats refresh after purchase");
        fetchStats();
      }
    }, 500);
  }, [fetchStats]);

  // Manual refresh button handler
  const handleManualRefresh = () => {
    console.log("Manual refresh requested");
    fetchStats();
  };

  // If still loading after initial attempt
  if (isLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justifyContent="center" alignItems="center" minH="50vh" direction="column">
          <Spinner size="xl" color="brand.500" mb={4} />
          <Text mb={4}>Loading blindbox statistics... (Render #{renderCount.current})</Text>
          <Button 
            onClick={handleManualRefresh} 
            size="sm" 
            colorScheme="brand"
            mt={4}
          >
            Retry Loading
          </Button>
          <Text fontSize="sm" color="gray.500" mt={4}>
            If loading takes too long, check that the backend server is running at port 8080
          </Text>
          
          {process.env.NODE_ENV !== 'production' && (
            <Button
              onClick={() => {
                console.log('Debug - Current state:', { stats, isLoading, error, renderCount: renderCount.current });
                toast({
                  title: 'Debug Info',
                  description: `Data: ${stats ? 'Present' : 'Null'}, Loading: ${isLoading}, Error: ${error ? error.message : 'None'}, Render: ${renderCount.current}`,
                  status: 'info',
                  duration: 5000,
                  isClosable: true,
                });
              }}
              size="xs"
              colorScheme="gray"
              mt={4}
              variant="outline"
            >
              Debug State
            </Button>
          )}
        </Flex>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error" borderRadius="md" mb={4}>
          <AlertIcon />
          <Box flex="1">
            <Text fontWeight="bold">{error.message || 'Failed to load blindbox statistics'}</Text>
            <Text fontSize="sm" mt={2}>
              Please check that the backend server is running on port 8080 and accessible.
            </Text>
            {process.env.NODE_ENV !== 'production' && (
              <Code mt={2} p={2} fontSize="xs" width="100%" overflowX="auto">
                {error.stack}
              </Code>
            )}
          </Box>
        </Alert>
        <Button 
          onClick={handleManualRefresh}
          colorScheme="brand" 
          mt={4}
        >
          Retry
        </Button>
      </Container>
    );
  }

  if (!stats) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="warning" borderRadius="md" mb={4}>
          <AlertIcon />
          No data available. Please check that the backend server is running and try again later.
        </Alert>
        <Button 
          onClick={handleManualRefresh}
          colorScheme="brand" 
          mt={4}
        >
          Retry
        </Button>
      </Container>
    );
  }

  // If we have stats, render the main content
  return (
    <Container maxW="container.xl" py={8}>
      <Heading as="h1" mb={6}>
        BlindBox Game
      </Heading>

      <Flex
        direction={{ base: 'column', lg: 'row' }}
        gap={8}
        mb={10}
      >
        {/* Left column - BlindBox card */}
        <Box flex="1" maxW={{ base: '100%', lg: '400px' }}>
          <BlindboxCard 
            cost={stats.stats.blindboxCost || 1} 
            onPurchaseComplete={handlePurchaseComplete} 
          />
        </Box>

        {/* Right column - Stats */}
        <Box flex="2">
          <Box mb={6}>
            <Heading size="md" mb={4}>Pool Status</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <StatsCard
                title="Total Pool Size"
                stat={`${formatNumber(stats.poolInfo.totalPoolSize || 0)} SOL`}
                helpText={`${formatNumber(stats.poolInfo.growth || 0)} SOL growth`}
                type={stats.poolInfo.growth && stats.poolInfo.growth >= 0 ? 'increase' : 'decrease'}
                icon={<FaCubes size="3em" />}
              />
              
              <StatsCard
                title="Current Mode"
                stat={stats.poolInfo.state || 'Normal'}
                helpText={`EV: ${formatNumber(stats.stats.normalExpectedValue || 0)} SOL`}
                icon={<FaSyncAlt size="3em" />}
              />
              
              <StatsCard
                title="System Fee"
                stat={`${stats.stats.systemFeePercentage || 0}%`}
                helpText={`${formatNumber(stats.poolInfo.totalSystemFees || 0)} SOL collected`}
                icon={<FaPercentage size="3em" />}
              />
            </SimpleGrid>
          </Box>

          <Box 
            bg={boxBg} 
            p={5} 
            borderRadius="lg" 
            boxShadow="sm"
            mb={6}
          >
            <Tabs variant="enclosed" colorScheme="brand">
              <TabList>
                <Tab>Theoretical Probabilities</Tab>
                <Tab>Actual Rates Analysis</Tab>
              </TabList>
              
              <TabPanels>
                <TabPanel px={0} pt={4}>
                  <Heading size="md" mb={4}>Item Probabilities</Heading>
                  
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    {/* Normal state items */}
                    <Box>
                      <Flex align="center" mb={2}>
                        <Text fontWeight="bold" mr={2}>Normal State</Text>
                        <Badge colorScheme="green">
                          EV: {formatNumber(stats.stats.normalExpectedValue || 0)} SOL
                        </Badge>
                      </Flex>
                      <TableContainer>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Item</Th>
                              <Th>Value</Th>
                              <Th>Chance</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {stats.items.normal.map((item: BlindboxItem, index: number) => (
                              <Tr key={index}>
                                <Td>
                                  <Badge colorScheme={
                                    item.name === 'Common' ? 'gray' :
                                    item.name === 'Standard' ? 'blue' :
                                    item.name === 'Uncommon' ? 'green' :
                                    item.name === 'Rare' ? 'purple' :
                                    item.name === 'Epic' ? 'pink' :
                                    'orange'
                                  }>
                                    {item.name}
                                  </Badge>
                                </Td>
                                <Td>{item.value.toFixed(2)} SOL</Td>
                                <Td>{(item.probability * 100).toFixed(0)}%</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </Box>

                    {/* Adjusted state items */}
                    <Box>
                      <Flex align="center" mb={2}>
                        <Text fontWeight="bold" mr={2}>Adjusted State</Text>
                        <Badge colorScheme="orange">
                          EV: {formatNumber(stats.stats.adjustedExpectedValue || 0)} SOL
                        </Badge>
                      </Flex>
                      <TableContainer>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Item</Th>
                              <Th>Value</Th>
                              <Th>Chance</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {stats.items.adjusted.map((item: BlindboxItem, index: number) => (
                              <Tr key={index}>
                                <Td>
                                  <Badge colorScheme={
                                    item.name === 'Common' ? 'gray' :
                                    item.name === 'Standard' ? 'blue' :
                                    item.name === 'Uncommon' ? 'green' :
                                    item.name === 'Rare' ? 'purple' :
                                    item.name === 'Epic' ? 'pink' :
                                    'orange'
                                  }>
                                    {item.name}
                                  </Badge>
                                </Td>
                                <Td>{item.value.toFixed(2)} SOL</Td>
                                <Td>{(item.probability * 100).toFixed(0)}%</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </SimpleGrid>
                </TabPanel>
                
                <TabPanel px={0} pt={4}>
                  <BlindboxRatesStats onRefresh={handleManualRefresh} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>

          {/* Transactions */}
          {stats.recentTransactions && stats.recentTransactions.length > 0 && (
            <TransactionList
              transactions={stats.recentTransactions}
            />
          )}
        </Box>
      </Flex>
    </Container>
  );
};

export default BlindboxPage; 